/**
 * WHO MAY CHANGE WHAT.
 *
 * Anthony: "Admin can edit all, so can service advisor. Technician cannot
 * edit phone number but can edit anything else not related to protocol.
 * Can update address, can't skip authorization as an example."
 *
 * There are TWO different rules in that sentence and they are worth keeping
 * apart, because one is a preference and the other is the law.
 *
 *   FIELD RULES are about tidiness. A technician standing at a car can fix
 *   a wrong address, because they are looking at the house. They should not
 *   be retyping the phone number, because the number is how the shop
 *   reaches somebody and a fat-fingered digit is a customer lost silently.
 *   This is a workflow rule, not a security boundary — a technician can
 *   already READ the number, so hiding the edit is about care, not secrecy.
 *
 *   PROTOCOL RULES are the shop's obligations. No work without an
 *   authorization. No bill without the work done. Those do not bend for a
 *   role, an admin included, because they are not about trust — they are
 *   about what the shop can defend afterwards. The last system let people
 *   click straight past them.
 *
 * WHERE THIS IS ENFORCED, honestly: in the interface. Row level security
 * decides which SHOP's rows you can touch, and it does that in the
 * database where it cannot be argued with. Field-level rules are not in the
 * database yet — Postgres does column-level grants and that is the right
 * home for them, but it needs a migration. Until then a determined person
 * with the browser console could patch a phone number. That is a tidiness
 * rule, so the gap is acceptable and written down rather than pretended
 * away. The protocol rules are the ones that must not live here alone, and
 * those are already constraints in the schema.
 */

export type Role = "admin" | "supervisor" | "advisor" | "technician" | "viewer";

/** Fields a person might click on. Named, so a typo is a build error. */
export type Field =
  | "customer.full_name" | "customer.phone" | "customer.email"
  | "customer.address" | "customer.notes"
  | "job.title" | "job.complaint" | "job.notes" | "job.quote_note";

const FULL_EDIT: Role[] = ["admin", "supervisor", "advisor"];

/** Nobody edits anything as a viewer. */
export function canEdit(role: Role, field: Field): boolean {
  if (role === "viewer") return false;
  if (FULL_EDIT.includes(role)) return true;

  // Technician: everything except the number the shop rings.
  if (role === "technician") return field !== "customer.phone";
  return false;
}

/** Said out loud when a field is not clickable, so it is not just inert. */
export function whyNot(role: Role, field: Field): string {
  if (role === "viewer") return "Your login can look but not change.";
  if (role === "technician" && field === "customer.phone") {
    return "Only the desk changes a phone number — it is how the shop reaches them.";
  }
  return "You cannot change this.";
}

/* ── protocol ─────────────────────────────────────────────────────────
   Not role-based. These are the shop's obligations and they apply to
   everybody, which is the whole point of them. */

export type Protocol = { allowed: boolean; because: string };

export function mayStartWork(f: { hasAuthorization: boolean; hasPricedLine: boolean }): Protocol {
  if (!f.hasPricedLine) {
    return { allowed: false, because: "Nothing is priced, so there is no figure anybody could have agreed to." };
  }
  if (!f.hasAuthorization) {
    return { allowed: false, because: "No authorization on file. Signed or spoken, it has to exist before the work does." };
  }
  return { allowed: true, because: "Priced and authorized." };
}

export function mayBill(f: { status: string }): Protocol {
  return ["completed", "invoiced", "paid"].includes(f.status)
    ? { allowed: true, because: "The work is done." }
    : { allowed: false, because: "The work is not finished yet." };
}

/* ── overriding protocol ──────────────────────────────────────────────
   Anthony: "service advisors and admin have a bypass option to close a
   job that didn't fulfill checks and balances. Technicians do not have
   this and get prompted and checked that they complete the appropriate
   steps."

   AN OVERRIDE IS NOT A BYPASS, and the difference is the whole design.

   A bypass makes the rule disappear, and a rule that can disappear
   silently was never a rule — it was a suggestion nobody could rely on.
   An override leaves the rule standing, requires somebody senior to say
   they are stepping past it, requires a REASON, and writes both to the
   job. The next person to open that job sees who decided and why.

   That matters most in the case this exists for: a shop that has to close
   a job with no authorization on file will one day be asked about it. The
   difference between "there is no record" and "Rosa closed it on the 3rd
   because the customer paid cash at the curb and left" is the difference
   between a problem and a note.

   A technician does not get the button. Not because they are less
   trusted, but because the prompt IS their job — being walked through
   what is missing is the check working, and a person who can dismiss the
   check is not being checked. */

export const canOverrideProtocol = (role: Role): boolean =>
  role === "admin" || role === "supervisor" || role === "advisor";

/** What a technician is told instead of being handed a way through. */
export const overrideHint = (role: Role): string =>
  canOverrideProtocol(role)
    ? "You can close it anyway, but the reason goes on the job."
    : "A service advisor or an admin can close it without these, and it will be recorded.";

/* ── ending a job is not a stage change ───────────────────────────────
   Anthony, 2026-09-15: "Technicians cannot cancel a job."

   THE DAY THIS WAS WRITTEN. Job 1747, a fuel injector replacement on
   Matthew Villa's Camry. He approved $450 on his own screen at 1:32pm.
   At 4:20pm the technician it was assigned to moved it straight from
   Quote to Cancelled. No reason was asked for and none was recorded, so
   the first anybody knew was the job being missing — the money the
   customer had already agreed to, gone, with nothing on the record
   saying who ended it or why.

   Every other stage a technician moves is a report of what happened:
   started, finished, on hold. Those are theirs, and they should be — the
   person with the car knows. Cancelling is not a report. It ends the
   shop's agreement with a customer, and that is the desk's call, the same
   way the desk owns the price and the authorization.

   So this is deliberately NOT a field rule. A field rule is about
   tidiness and lives in the interface. This one survives the interface:
   `setStatus` refuses it whatever screen asked, because the button being
   hidden is not the same as the move being impossible, and 1747 is what
   it costs to learn that difference. */

export const mayCancel = (role: Role): Protocol =>
  role === "technician"
    ? { allowed: false, because:
        "A technician cannot cancel a job. Tell the desk what happened and they will end it — cancelling drops work a customer may already have agreed to pay for." }
    : role === "viewer"
    ? { allowed: false, because: "Your login can look but not change." }
    : { allowed: true, because: "The desk ends a job." };
